import {Checkbox, IconButton, openDeleteDialog, Tooltip} from 'platform/components';
import {Box, Heading, HStack, Space, Text, VStack, Show} from 'platform/foundation';

import {Fragment} from 'react';

import {isNil, isNotNil, not} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {getInvoiceName, MappedBasketItem, TITLE_TOOLTIP_OPEN_DELAY} from '@dms/shared';

import {EMPTY_PLACEHOLDER, getRandomId, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {LabourBasketItem} from '../types/LabourBasketItem';
import {LabourBasketItemEditingDetails} from '../types/LabourBasketItemEditingDetails';

interface AfterSalesLabourBasketItemTitleWithActionsProps<
  T extends LabourBasketItem,
> extends RequiredTestIdProps {
  item: MappedBasketItem<T>;
  onSelect: (itemId: string, isSelected: boolean) => void;
  onEdit: (editingDetails: LabourBasketItemEditingDetails) => void;
  onDelete: (itemId: string) => Promise<void>;
  hasPendingRequest: boolean;
}

export function AfterSalesLabourBasketItemTitleWithActions<T extends LabourBasketItem>(
  props: AfterSalesLabourBasketItemTitleWithActionsProps<T>
) {
  const invoiceName = getInvoiceName(props.item) ?? props.item.name;

  const handleEditItem = () => {
    if (isNil(props.item.id)) {
      throw new Error("Missing item's id");
    }

    props.onEdit({
      itemId: props.item.id,
      itemName: invoiceName,
      priceTypeName: props.item.priceTypeName,
    });
  };

  const handleDeleteItem = () => {
    openDeleteDialog({
      name: invoiceName,
      onConfirm: () => props.onDelete(props.item.id),
      'data-testid': suffixTestId('delete', props),
    });
  };

  // TODO https://carvago.atlassian.net/browse/T20-58988
  const tooltip = (
    <>
      {isArray(props.item.tooltip) && isNotNil(props.item.tooltip)
        ? props.item.tooltip?.map((item, index) => (
            <Fragment key={item.label}>
              <Show when={index > 0}>
                <Space vertical={2} />
              </Show>
              <Text size="xSmall" alternative color="white">
                {item.label}
              </Text>
              <Text size="xSmall" color="white">
                {item.value || EMPTY_PLACEHOLDER}
              </Text>
            </Fragment>
          ))
        : null}
    </>
  );

  return (
    <VStack spacing={1}>
      <HStack align="center" spacing={2}>
        <Checkbox
          isDisabled={props.hasPendingRequest}
          value={props.item.isSelected}
          onChange={(isSelected) => props.onSelect(props.item.id, isSelected)}
          data-testid={suffixTestId('isSelected', props)}
        />
        <Box flexGrow={1} minWidth={0}>
          <Tooltip
            label={props.item.number}
            placement="top"
            openDelay={TITLE_TOOLTIP_OPEN_DELAY}
            hasAutoWidth
          >
            <Heading size={4} isSingleLine data-testid={suffixTestId('number', props)}>
              {props.item.number}
            </Heading>
          </Tooltip>
        </Box>
        <HStack justify="flex-end" shrink={0} spacing={1}>
          <Show key={`basketItemTooltip-${getRandomId()}`} when={tooltip}>
            <Tooltip placement="top" description={tooltip}>
              <IconButton
                icon="action/info_outline"
                size="small"
                priority="secondary"
                data-testid={suffixTestId('tooltip', props)}
              />
            </Tooltip>
          </Show>
          <IconButton
            isDisabled={props.hasPendingRequest || not(props.item.itemEditingAllowed)}
            icon="image/edit"
            size="small"
            priority="secondary"
            onClick={handleEditItem}
            data-testid={suffixTestId('edit', props)}
          />
          <IconButton
            isDisabled={props.hasPendingRequest}
            icon="navigation/close"
            size="small"
            priority="primary"
            severity="danger"
            onClick={handleDeleteItem}
            data-testid={suffixTestId('delete', props)}
          />
        </HStack>
      </HStack>
      <Text size="xSmall" color="tertiary" data-testid={suffixTestId('name', props)}>
        {invoiceName}
      </Text>
      <Space vertical={2} />
    </VStack>
  );
}
